import { api, USE_MOCK_DATA } from '@/lib/api';
import {
  summariseCaptureResults,
  type CaptureEnvelope,
  type CaptureResponse,
  type CaptureResultRow,
} from '@/data/capture-model';

/* ----------------------------------------------------------------------------
 * Outreach P0 — owner capture data layer.
 *
 * POST /v1/outreach/capture — SESSION-COOKIE authenticated, via the SAME
 * shared `api` client every other authed write in src/data/sources.ts uses
 * (credentials:'include' + CSRF double-submit, see src/lib/api.ts). There is
 * NO HMAC/API key in this module or anywhere in the browser — that stays
 * server-side per the locked P0 contract (planning-gate-outreach-cockpit
 * amendment A7). This module never scrapes and never fetches a directory; it
 * only posts whatever the owner (or Claude-in-Chrome, on the owner's behalf,
 * from a tab the owner opened) pasted, once normalised by capture-model.ts.
 *
 * The pure JSON parse/normalise logic lives in the sibling, dependency-free
 * data/capture-model.ts (re-exported below) so it stays unit-testable under
 * `node --test` exactly like data/support-model.ts and
 * data/content-generate-model.ts — this file adds only the network edge.
 * -------------------------------------------------------------------------- */

export {
  CAPTURED_VIA_OPTIONS,
  DEFAULT_CAPTURED_VIA,
  CaptureParseError,
  parseCaptureText,
  normaliseCapture,
  summariseCaptureResults,
  describeCaptureSummary,
} from '@/data/capture-model';
export type {
  CapturedVia,
  CaptureLeadInput,
  CaptureEnvelope,
  CaptureOutcome,
  CaptureResultRow,
  CaptureSummary,
  CaptureResponse,
} from '@/data/capture-model';

/**
 * Submit a normalised capture envelope to POST /v1/outreach/capture. Rides
 * the app's normal session cookie via the shared `api` client (credentials:
 * 'include' + CSRF double-submit token) — the exact mechanism every other
 * authed mutation in src/data/sources.ts uses. This function never holds or
 * sends an HMAC/API key; it never calls out to any directory or third-party
 * site; it never sends anything to a prospect (no SES, no send, anywhere in
 * P0).
 */
export async function submitCapture(payload: CaptureEnvelope): Promise<CaptureResponse> {
  if (USE_MOCK_DATA) {
    // Deterministic, honest mock: first lead "created", the rest "duplicate"
    // — never a fake success-all, so the page's mixed-outcome rendering is
    // exercisable without a backend.
    const results: CaptureResultRow[] = payload.leads.map((_, i) =>
      i === 0
        ? { outcome: 'created', lead_id: 1000 + i }
        : { outcome: 'duplicate', reason: 'Mock: matches an existing lead (email or domain).' },
    );
    return { results, summary: summariseCaptureResults(results) };
  }
  return api.post<CaptureResponse>('/outreach/capture', payload);
}
